import React, { useState, useMemo } from 'react';
import { UserWorkspace, UserSavedScenario, StudioSession } from '../../types';
import {
  Bookmark,
  Star,
  Trash2,
  ArrowUpRight,
  Download,
  ShieldCheck,
  Lock,
  Layers,
  FileText,
  Bell,
  Sparkles,
  TrendingUp,
  DollarSign,
  Zap,
} from 'lucide-react';

interface UserWorkspaceViewProps {
  workspace: UserWorkspace | null;
  session: StudioSession;
  onLoadScenarioIntoSandbox: (scenario: UserSavedScenario) => void;
  onDeleteScenario: (scenarioId: string) => Promise<void>;
  onExportIcMemo: (canonicalId: string, scenarioId?: string) => void;
  onOpenIcMemoModal: (canonicalId: string, scenario?: UserSavedScenario) => void;
  onOpenAuthModal: () => void;
  onSelectCompById: (canonicalId: string) => void;
}

export const UserWorkspaceView: React.FC<UserWorkspaceViewProps> = ({
  workspace,
  session,
  onLoadScenarioIntoSandbox,
  onDeleteScenario,
  onExportIcMemo,
  onOpenIcMemoModal,
  onOpenAuthModal,
  onSelectCompById,
}) => {
  const [activeSubTab, setActiveSubTab] = useState<'scenarios' | 'starred' | 'custom_comps'>('scenarios');
  const [activeAlertTargetId, setActiveAlertTargetId] = useState<string | null>(null);

  if (!session.authenticated) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center space-y-4 bg-[#06090e]">
        <div className="w-16 h-16 rounded-2xl bg-[#2979ff]/10 border border-[#2979ff]/30 flex items-center justify-center text-[#2979ff]">
          <Lock className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-bold text-white">Private Tenant Workspace Locked</h2>
        <p className="text-sm text-slate-400 max-w-md leading-relaxed">
          Sign in with your AIxEnergy Ghost account to save custom underwriting scenarios, internal diligence commentary, deal stage trackers, and proprietary off-market comps.
        </p>
        <button
          onClick={onOpenAuthModal}
          className="px-5 py-2.5 rounded-lg bg-[#2979ff] hover:bg-[#1c68e8] text-white font-semibold text-xs shadow-lg shadow-[#2979ff]/20 transition"
        >
          Sign In with Ghost SSO
        </button>
      </div>
    );
  }

  const scenarios = workspace?.scenarios || [];
  const annotations = workspace?.annotations || [];
  const customComps = workspace?.custom_comps || [];

  // Compute portfolio aggregate metrics across saved scenarios
  const portfolioStats = useMemo(() => {
    if (scenarios.length === 0) {
      return {
        totalCapexUsd: 0,
        avgYieldOnCost: 0,
        avgLeveredIrr: 0,
        totalMw: 0,
      };
    }

    const totalCapex = scenarios.reduce((acc, sc) => acc + (sc.total_capex_usd || 0), 0);
    const totalMw = scenarios.reduce((acc, sc) => acc + (sc.it_load_mw || 0), 0);
    const avgYield = scenarios.reduce((acc, sc) => acc + (sc.unlevered_yield_on_cost_pct || 0), 0) / scenarios.length;
    const avgIrr = scenarios.reduce((acc, sc) => acc + (sc.levered_irr_5yr_pct || 0), 0) / scenarios.length;

    return {
      totalCapexUsd: totalCapex,
      avgYieldOnCost: avgYield,
      avgLeveredIrr: avgIrr,
      totalMw,
    };
  }, [scenarios]);

  return (
    <div className="flex flex-col h-full bg-[#06090e] overflow-y-auto p-4 space-y-4 select-none">
      {/* 1. Header & RLS Tenant Badge */}
      <div className="p-4 rounded-xl bg-[#0c121e] border border-[#1a253b] flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <div className="flex items-center space-x-2">
            <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 flex items-center font-bold">
              <ShieldCheck className="w-3 h-3 mr-1" />
              RLS TENANT-ISOLATED
            </span>
            <span className="text-xs text-slate-400 font-mono">
              USER: {session.email || session.user_id}
            </span>
          </div>
          <h2 className="text-lg font-bold text-white mt-1">
            Institutional Diligence Workspace &amp; IC Deal Room
          </h2>
          <p className="text-xs text-slate-400">
            Tenant-isolated underwrites, private diligence notes, and 1-click Investment Committee Deal Memos.
          </p>
        </div>

        {/* Sub-tabs */}
        <div className="flex items-center space-x-1 bg-[#06090e] p-1 rounded-lg border border-[#1a253b] text-xs">
          <button
            onClick={() => setActiveSubTab('scenarios')}
            className={`px-3 py-1.5 rounded-md font-medium transition ${
              activeSubTab === 'scenarios'
                ? 'bg-[#162035] text-white font-semibold border border-[#2979ff]/40 text-[#2979ff]'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Saved Underwrites ({scenarios.length})
          </button>
          <button
            onClick={() => setActiveSubTab('starred')}
            className={`px-3 py-1.5 rounded-md font-medium transition ${
              activeSubTab === 'starred'
                ? 'bg-[#162035] text-white font-semibold border border-[#2979ff]/40 text-[#2979ff]'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Watchlist &amp; Alerts ({annotations.length})
          </button>
          <button
            onClick={() => setActiveSubTab('custom_comps')}
            className={`px-3 py-1.5 rounded-md font-medium transition ${
              activeSubTab === 'custom_comps'
                ? 'bg-[#162035] text-white font-semibold border border-[#2979ff]/40 text-[#2979ff]'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Proprietary Comps ({customComps.length})
          </button>
        </div>
      </div>

      {/* 2. Portfolio Aggregate Telemetry Ribbon (Sticky Value-Add) */}
      {scenarios.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-[#0c121e] border border-[#1a253b] font-mono">
            <span className="text-[10px] uppercase text-slate-500 block">Total Pipeline Capex</span>
            <span className="text-base font-bold text-white mt-0.5">
              ${(portfolioStats.totalCapexUsd / 1_000_000_000).toFixed(2)}B
            </span>
            <span className="text-[10px] text-slate-500 block font-sans">
              Across {scenarios.length} saved models
            </span>
          </div>

          <div className="p-3 rounded-lg bg-[#0c121e] border border-[#1a253b] font-mono">
            <span className="text-[10px] uppercase text-slate-500 block">Total Contracted Load</span>
            <span className="text-base font-bold text-[#2979ff] mt-0.5">
              {portfolioStats.totalMw.toLocaleString()} MW
            </span>
            <span className="text-[10px] text-slate-500 block font-sans">
              Gigawatt IT capacity
            </span>
          </div>

          <div className="p-3 rounded-lg bg-[#0c121e] border border-[#1a253b] font-mono">
            <span className="text-[10px] uppercase text-slate-500 block">Portfolio Avg Yield</span>
            <span className="text-base font-bold text-amber-400 mt-0.5">
              {portfolioStats.avgYieldOnCost.toFixed(2)}%
            </span>
            <span className="text-[10px] text-slate-500 block font-sans">
              Unlevered Yield on Cost
            </span>
          </div>

          <div className="p-3 rounded-lg bg-[#0c121e] border border-[#1a253b] font-mono">
            <span className="text-[10px] uppercase text-slate-500 block">Portfolio Avg 5-Yr IRR</span>
            <span className="text-base font-bold text-emerald-400 mt-0.5">
              {portfolioStats.avgLeveredIrr.toFixed(1)}%
            </span>
            <span className="text-[10px] text-slate-500 block font-sans">
              Levered Equity Return
            </span>
          </div>
        </div>
      )}

      {/* Tab 1: Saved Scenarios */}
      {activeSubTab === 'scenarios' && (
        <div className="space-y-3">
          {scenarios.length === 0 ? (
            <div className="p-12 rounded-xl bg-[#0c121e] border border-[#1a253b] text-center text-slate-400 space-y-2">
              <Bookmark className="w-8 h-8 mx-auto text-slate-600" />
              <p className="text-xs">No underwriting scenarios saved yet.</p>
              <p className="text-[11px] text-slate-500 font-mono">
                Go to the Underwriting Sandbox, adjust parameters, and click &quot;Save Scenario&quot;.
              </p>
            </div>
          ) : (
            scenarios.map((sc) => (
              <div
                key={sc.scenario_id}
                className="p-4 rounded-xl bg-[#0c121e] border border-[#1a253b] hover:border-slate-700 transition flex flex-col md:flex-row md:items-center justify-between gap-3"
              >
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-mono text-xs font-bold text-[#2979ff]">
                      {sc.canonical_project_id}
                    </span>
                    <span className="text-xs text-slate-500">&bull;</span>
                    <span className="text-xs text-slate-400 font-mono">{sc.updated_at?.split('T')[0]}</span>
                    <span className="text-[10px] px-1.5 py-0.2 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 font-mono">
                      {sc.it_load_mw} MW
                    </span>
                  </div>
                  <h3 className="text-sm font-bold text-white mt-0.5">{sc.scenario_name}</h3>
                  {sc.user_notes && (
                    <p className="text-xs text-slate-400 italic line-clamp-1 mt-0.5">&quot;{sc.user_notes}&quot;</p>
                  )}
                </div>

                <div className="flex items-center space-x-4">
                  <div className="grid grid-cols-3 gap-3 bg-[#06090e] p-2.5 rounded-lg border border-[#1a253b] font-mono text-xs">
                    <div>
                      <span className="text-[10px] text-slate-500 block">Total Capex</span>
                      <span className="font-bold text-white">${(sc.total_capex_usd / 1_000_000).toFixed(0)}M</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-500 block">Yield on Cost</span>
                      <span className="font-bold text-amber-400">{sc.unlevered_yield_on_cost_pct.toFixed(2)}%</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-500 block">5-Yr IRR</span>
                      <span className="font-bold text-cyan-400">{sc.levered_irr_5yr_pct.toFixed(1)}%</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {/* Load into Sandbox */}
                    <button
                      onClick={() => onLoadScenarioIntoSandbox(sc)}
                      className="px-2.5 py-1.5 bg-[#121a2d] hover:bg-[#1a253b] text-slate-200 text-xs rounded-lg font-medium flex items-center transition border border-[#1a253b]"
                      title="Load model into Underwriting Sandbox"
                    >
                      <ArrowUpRight className="w-3.5 h-3.5 mr-1 text-[#2979ff]" />
                      <span>Sandbox</span>
                    </button>

                    {/* View IC Memo Modal */}
                    <button
                      onClick={() => onOpenIcMemoModal(sc.canonical_project_id, sc)}
                      className="px-2.5 py-1.5 bg-[#2979ff]/15 hover:bg-[#2979ff]/25 text-[#2979ff] text-xs rounded-lg font-medium flex items-center transition border border-[#2979ff]/30"
                      title="Preview Investment Committee Memo Pack"
                    >
                      <FileText className="w-3.5 h-3.5 mr-1" />
                      <span>IC Memo</span>
                    </button>

                    {/* Direct 1-Click PDF Download */}
                    <button
                      onClick={() => onExportIcMemo(sc.canonical_project_id, sc.scenario_id)}
                      className="p-1.5 bg-[#121a2d] hover:bg-[#1a253b] text-slate-300 hover:text-white text-xs rounded-lg transition border border-[#1a253b]"
                      title="Download Institutional IC Deal Memo PDF directly"
                    >
                      <Download className="w-3.5 h-3.5 text-[#2979ff]" />
                    </button>

                    {/* Delete */}
                    <button
                      onClick={() => onDeleteScenario(sc.scenario_id)}
                      className="p-1.5 bg-[#121a2d] hover:bg-rose-950/40 text-slate-400 hover:text-rose-400 text-xs rounded-lg transition border border-[#1a253b]"
                      title="Delete Scenario"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Tab 2: Tracked Assets & Alerts */}
      {activeSubTab === 'starred' && (
        <div className="space-y-3">
          {annotations.length === 0 ? (
            <div className="p-12 rounded-xl bg-[#0c121e] border border-[#1a253b] text-center text-slate-400">
              <Star className="w-8 h-8 mx-auto text-slate-600 mb-2" />
              <p className="text-xs">No tracked assets or diligence notes recorded.</p>
              <p className="text-[11px] text-slate-500 font-mono">
                Open any asset dossier and click &quot;Star Asset&quot; or save private diligence notes.
              </p>
            </div>
          ) : (
            annotations.map((ann) => (
              <div
                key={ann.canonical_project_id}
                className="p-4 rounded-xl bg-[#0c121e] border border-[#1a253b] hover:border-[#2979ff]/60 transition space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div
                    className="flex items-center space-x-2 cursor-pointer"
                    onClick={() => onSelectCompById(ann.canonical_project_id)}
                  >
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                    <span className="font-mono text-xs font-bold text-white hover:text-[#2979ff] transition">
                      {ann.canonical_project_id}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30 font-mono uppercase">
                      {ann.deal_stage.replace(/_/g, ' ')}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-[#162035] text-slate-300 font-mono">
                      Rating: {ann.internal_rating || 'TIER_1'}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2">
                    {/* Alert Trigger Indicator */}
                    <button
                      onClick={() =>
                        setActiveAlertTargetId(
                          activeAlertTargetId === ann.canonical_project_id ? null : ann.canonical_project_id
                        )
                      }
                      className={`flex items-center space-x-1 px-2.5 py-1 rounded-lg text-xs font-mono transition border ${
                        activeAlertTargetId === ann.canonical_project_id
                          ? 'bg-amber-500/20 text-amber-400 border-amber-500/40'
                          : 'bg-[#121a2d] text-slate-400 hover:text-white border-[#1a253b]'
                      }`}
                      title="Configure target price and milestone alert triggers"
                    >
                      <Bell className="w-3.5 h-3.5" />
                      <span>Alert Triggers</span>
                    </button>

                    {/* 1-Click IC Memo */}
                    <button
                      onClick={() => onOpenIcMemoModal(ann.canonical_project_id)}
                      className="px-2.5 py-1 bg-[#2979ff]/15 hover:bg-[#2979ff]/25 text-[#2979ff] text-xs rounded-lg font-medium flex items-center transition border border-[#2979ff]/30 font-mono"
                    >
                      <FileText className="w-3.5 h-3.5 mr-1" />
                      <span>IC Memo</span>
                    </button>
                  </div>
                </div>

                {/* Alert Trigger Drawer */}
                {activeAlertTargetId === ann.canonical_project_id && (
                  <div className="p-3 rounded-lg bg-[#06090e] border border-amber-500/30 text-xs font-mono space-y-2">
                    <div className="flex items-center justify-between text-amber-400">
                      <span className="font-bold flex items-center">
                        <Bell className="w-3.5 h-3.5 mr-1.5" />
                        Milestone &amp; Price Alert Rules for {ann.canonical_project_id}
                      </span>
                      <span className="text-[10px] text-emerald-400">STATUS: MONITORING</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px] text-slate-300">
                      <div className="p-2 rounded bg-[#0c121e] border border-[#1a253b]">
                        <span className="text-slate-500 block">Buyout Alert Trigger</span>
                        <span className="text-white font-bold">&lt; $250k / MW</span>
                      </div>
                      <div className="p-2 rounded bg-[#0c121e] border border-[#1a253b]">
                        <span className="text-slate-500 block">Queue Delay Alert</span>
                        <span className="text-rose-400 font-bold">&gt; 12 Months Slippage</span>
                      </div>
                      <div className="p-2 rounded bg-[#0c121e] border border-[#1a253b]">
                        <span className="text-slate-500 block">New Deed Filing</span>
                        <span className="text-cyan-400 font-bold">Instant Email Alert</span>
                      </div>
                    </div>
                  </div>
                )}

                {ann.private_notes && (
                  <p className="text-xs text-slate-300 bg-[#06090e] p-2.5 rounded-lg border border-[#1a253b]">
                    &quot;{ann.private_notes}&quot;
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Tab 3: Proprietary Off-Market Comps */}
      {activeSubTab === 'custom_comps' && (
        <div className="p-4 rounded-xl bg-[#0c121e] border border-[#1a253b] space-y-4">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-200">
              Proprietary Off-Market Comp Submissions
            </h3>
            <p className="text-[11px] text-slate-400">
              Submit proprietary transaction benchmarks into your private workspace without leaking terms to the public registry.
            </p>
          </div>

          <div className="p-8 rounded-lg border border-dashed border-[#1a253b] bg-[#06090e] text-center space-y-2">
            <Layers className="w-6 h-6 text-slate-500 mx-auto" />
            <p className="text-xs text-slate-300 font-medium">No custom off-market comps entered yet.</p>
            <p className="text-[11px] text-slate-500 max-w-sm mx-auto font-mono">
              Custom comps are strictly bound to your Ghost Member UUID in <code>deal_user.user_custom_comps</code>.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
